"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { solvePow } from "./solver.js";
export function PowCaptcha({ onSuccess, onError, auto = true, endpoint = "/api/captcha/pow", }) {
    const [status, setStatus] = useState("loading");
    const [progress, setProgress] = useState(0);
    const runRef = useRef(0);
    const doneRef = useRef(false);
    /** 当前这轮求解的句柄; 换题或卸载时必须 cancel, 否则 Worker 会留着继续烧 CPU */
    const handleRef = useRef(null);
    /** 用 ref 存最新回调 —— 与 SliderCaptcha 同一个模式, 理由也一样:
     *  调用方传内联箭头函数是最常见写法, 直接进依赖数组会让每次 render 都重跑 effect、重新出题。
     *  PoW 这边尤其致命: 求解成功 → 调用方 setState → 本组件 props 变 → load 重建 → effect 重跑
     *  → 换一道新题重算 → 又成功 …… 成环, 每轮烧一次出题配额, 几十秒就把用户自己限流到 429。
     *  (5ff 2026-08-18 线上踩到: /api/captcha/pow 从 15 次/小时涨到 240 次/小时并开始出 429。) */
    const cbRef = useRef({ onSuccess, onError });
    cbRef.current = { onSuccess, onError };
    const solve = useCallback((ch, myRun) => {
        setStatus("solving");
        setProgress(0);
        handleRef.current?.cancel();
        handleRef.current = solvePow({ salt: ch.salt, challenge: ch.challenge, maxnumber: ch.maxnumber }, {
            onProgress: (ratio) => {
                if (runRef.current !== myRun)
                    return;
                setProgress(Math.min(99, Math.round(ratio * 100)));
            },
            onDone: (n) => {
                if (doneRef.current || runRef.current !== myRun)
                    return;
                doneRef.current = true;
                const payload = btoa(JSON.stringify({ algorithm: ch.algorithm, challenge: ch.challenge, number: n, salt: ch.salt, signature: ch.signature, expires: ch.expires }));
                setProgress(100);
                setStatus("done");
                cbRef.current.onSuccess(payload);
            },
            onFail: () => {
                if (runRef.current !== myRun)
                    return;
                setStatus("error");
                cbRef.current.onError?.("验证失败，请重试");
            },
        });
    }, []);
    const load = useCallback(async () => {
        const myRun = ++runRef.current;
        doneRef.current = false;
        setStatus("loading");
        try {
            const res = await fetch(endpoint, { cache: "no-store" });
            if (runRef.current !== myRun)
                return;
            const data = await res.json();
            if (runRef.current !== myRun)
                return;
            if (data.ok && data.challenge)
                solve(data, myRun);
            else {
                setStatus("error");
                cbRef.current.onError?.(data.message || "验证加载失败");
            }
        }
        catch {
            if (runRef.current !== myRun)
                return;
            setStatus("error");
            cbRef.current.onError?.("验证加载失败，请刷新");
        }
    }, [solve, endpoint]);
    useEffect(() => {
        if (auto)
            load();
        return () => {
            runRef.current = -1;
            handleRef.current?.cancel();
            handleRef.current = null;
        };
    }, [auto, load]);
    return (_jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "6px 0" }, children: [_jsx("div", { style: { display: "flex", alignItems: "center", gap: 8, color: "#8a8698", fontSize: 13 }, children: status !== "error" ? (_jsxs(_Fragment, { children: [_jsx("span", { style: {
                                width: 14, height: 14, borderRadius: "50%",
                                border: "2px solid rgba(92,73,208,0.25)", borderTopColor: "#5c49d0",
                                display: "inline-block", animation: status === "done" ? "none" : "yzm-pow-spin 0.7s linear infinite",
                            } }), status === "done" ? "安全验证通过" : "安全验证中…"] })) : (_jsx("button", { type: "button", onClick: () => load(), style: { color: "#e8524a", background: "none", border: "none", cursor: "pointer", fontSize: 13 }, children: "\u9A8C\u8BC1\u5931\u8D25\uFF0C\u70B9\u51FB\u91CD\u8BD5" })) }), _jsx("div", { style: { width: 200, height: 4, borderRadius: 2, background: "rgba(0,0,0,0.08)", overflow: "hidden" }, children: _jsx("div", { style: { width: `${progress}%`, height: "100%", background: status === "done" ? "#39b54a" : "#5c49d0", transition: "width 120ms linear" } }) }), _jsx("style", { children: `@keyframes yzm-pow-spin{to{transform:rotate(360deg)}}` })] }));
}
